import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import PayrollLayout from "../../layouts/PayrollLayout";

export default function MyPayrolls() {
  const API_URL = "https://hrmsnew.prepstripe.com/api";
  const token = getToken();

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);

  // Filters
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  const months = [
    { value: "", label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = [
    { value: "", label: "All Years" },
    { value: "2025", label: "2025" },
    { value: "2024", label: "2024" },
    { value: "2023", label: "2023" },
    { value: "2022", label: "2022" },
  ];

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        per_page: perPage,
        page,
      };

      if (year) params.year = parseInt(year);
      if (month) params.month = parseInt(month);

      const response = await api.get("/payroll/my-payrolls", { params });
      const data = response.data;

      setRecords(Array.isArray(data) ? data : data.data || []);
      setTotal(data.meta?.total || data.length || 0);
    } catch (err) {
      console.error("Error loading records:", err);
      setError(err.response?.data?.message || "Failed to load payroll records");
    } finally {
      setLoading(false);
    }
  }, [api, year, month, perPage, page]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const downloadPayslip = async (recordId) => {
    try {
      const response = await api.get(
        `/payroll/monthly-payrolls/${recordId}/download`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `payslip-${recordId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Error downloading payslip:", err);
      setError("Failed to download payslip");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: {
        bg: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
        label: "Draft",
      },
      processed: {
        bg: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
        label: "Processed",
      },
      approved: {
        bg: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
        label: "Approved",
      },
      rejected: {
        bg: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
        label: "Rejected",
      },
      paid: {
        bg: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
        label: "Paid",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <PayrollLayout title="My Payrolls">
      <div className="flex flex-col gap-y-3 font-poppins">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <h2 className="text-xl font-semibold dark:text-white mb-4">
            My Payroll Records
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            View your payroll records and download payslips for different
            months.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">
                Year
              </label>
              <select
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">
                Month
              </label>
              <select
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">
                Per Page
              </label>
              <input
                type="number"
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value) || 12)}
                min="1"
                max="50"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setPage(1);
                  loadRecords();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Records Grid */}
        {loading ? (
          <div className="bg-white dark:bg-gray-900 p-8 mx-2 rounded-2xl shadow text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              Loading your payroll records...
            </p>
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 p-8 mx-2 rounded-2xl shadow text-center">
            <div className="text-gray-500 dark:text-gray-400">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-lg font-medium">No payroll records found</p>
              <p className="text-sm">
                You don't have any payroll records for the selected period.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mx-2">
            {records.map((record) => (
              <div
                key={record.id}
                className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold dark:text-white">
                      {
                        months.find(
                          (m) =>
                            m.value ===
                            record.month?.toString().padStart(2, "0"),
                        )?.label
                      }{" "}
                      {record.year}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(record.created_at)}
                    </p>
                  </div>
                  {getStatusBadge(record.status)}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Basic Salary:
                    </span>
                    <span className="font-medium dark:text-white">
                      {formatCurrency(record.basic_salary)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Gross Salary:
                    </span>
                    <span className="font-medium dark:text-white">
                      {formatCurrency(record.gross_salary)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Deductions:
                    </span>
                    <span className="font-medium dark:text-white">
                      {formatCurrency(record.deductions || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t pt-2">
                    <span className="text-gray-800 dark:text-gray-200">
                      Net Pay:
                    </span>
                    <span className="text-green-600 dark:text-green-400">
                      {formatCurrency(record.net_pay)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => downloadPayslip(record.id)}
                    disabled={
                      record.status !== "approved" && record.status !== "paid"
                    }
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Download Payslip
                  </button>
                  <button
                    onClick={() =>
                      window.open(
                        `/api/payroll/monthly-payrolls/${record.id}/preview`,
                        "_blank",
                      )
                    }
                    className="px-3 py-2 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Preview
                  </button>
                </div>

                {record.status === "draft" && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Payslip will be available once processed
                  </p>
                )}
                {record.status === "processed" && (
                  <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">
                    Awaiting approval
                  </p>
                )}
                {record.status === "rejected" && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                    Payroll was rejected
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > perPage && (
          <div className="flex items-center justify-between mx-2">
            <div className="text-sm dark:text-white">
              Page {page} of {Math.ceil(total / perPage)} ({total} total
              records)
            </div>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white rounded"
              >
                Previous
              </button>
              <button
                disabled={page >= Math.ceil(total / perPage)}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white rounded"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-2">
            {error}
          </div>
        )}
      </div>
    </PayrollLayout>
  );
}
